/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

/** Report kinds the generators can render — mirrors the API `report_type` enum. */
export const REPORT_TYPES = [
  'client_progress',
  'attendance',
  'revenue',
  'itp',
  'monthly_summary',
  'quarterly_review',
  'annual_report',
  'therapist_performance',
  'clinical_outcomes',
  'profit_loss',
  'tax_report',
  'revenue_forecast',
  'payroll_summary',
  'intake_assessment',
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

/** Report types that require a `YYYY-MM-DD` date range (others auto-derive theirs). */
const RANGE_REPORTS: readonly ReportType[] = ['attendance', 'revenue'];

/** Job lifecycle — mirrors the API `report_job_status` enum. */
export const REPORT_JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export type ReportJobStatus = (typeof REPORT_JOB_STATUSES)[number];

/** `YYYY-MM-DD` calendar day (the report screens collect dates, not timestamps). */
const isoDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');

const reportBaseSchema = z.object({
  reportType: z.enum(REPORT_TYPES),
  clientId: z.string().uuid('Select a client').optional(),
  planId: z.string().uuid('Select a plan').optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});
type ReportBase = z.infer<typeof reportBaseSchema>;

/** Add a single custom issue against one field. */
function fieldIssue(ctx: z.RefinementCtx, field: keyof ReportBase, message: string): void {
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
}

/** Require the scope field (client / plan) that the chosen report type needs. */
function checkScope(value: ReportBase, ctx: z.RefinementCtx): void {
  if (value.reportType === 'client_progress' && value.clientId === undefined) {
    fieldIssue(ctx, 'clientId', 'Select a client');
  }
  if (value.reportType === 'intake_assessment' && value.clientId === undefined) {
    fieldIssue(ctx, 'clientId', 'Select a client');
  }
  if (value.reportType === 'itp' && value.planId === undefined) {
    fieldIssue(ctx, 'planId', 'Select a plan');
  }
}

/** Require + order the date range for range-based report types. */
function checkRange(value: ReportBase, ctx: z.RefinementCtx): void {
  if (RANGE_REPORTS.includes(value.reportType)) {
    if (value.dateFrom === undefined) {
      fieldIssue(ctx, 'dateFrom', 'Pick a start date');
    }
    if (value.dateTo === undefined) {
      fieldIssue(ctx, 'dateTo', 'Pick an end date');
    }
  }
  if (value.dateFrom !== undefined && value.dateTo !== undefined && value.dateFrom > value.dateTo) {
    fieldIssue(ctx, 'dateTo', 'End date must be on or after the start date');
  }
}

/**
 * Generate a report (Figma Reports screens). The shape is validated identically
 * on the FE form (RHF) and the API DTO. A date range is required for the
 * range-based reports; `client_progress` additionally needs a client and `itp`
 * a plan. The service rejects a `dateFrom` after `dateTo`.
 */
export const GenerateReportSchema = reportBaseSchema.superRefine((value, ctx) => {
  checkScope(value, ctx);
  checkRange(value, ctx);
});
export type GenerateReportInput = z.infer<typeof GenerateReportSchema>;
